import { FunctionComponent } from 'react'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { IDataQualityBucket, IDataQualityMix } from '../../../shared/services/generated-report/generated-report.model'

//Shared renderer for the data-quality mix disclosure, used by the GHG report and the SECR annex
//so both documents present the same table for the same numbers.

const COLUMNS: { key: keyof Omit<IDataQualityMix, 'tiers'>, label: string }[] = [
  { key: 'scopeOne', label: 'Scope 1' },
  { key: 'scopeTwoLocation', label: 'Scope 2 (location)' },
  { key: 'scopeTwoMarket', label: 'Scope 2 (market)' },
  { key: 'scopeThree', label: 'Scope 3' },
  { key: 'totalLocation', label: 'Total (location)' },
  { key: 'totalMarket', label: 'Total (market)' },
]

const cellValue = (bucket: IDataQualityBucket, tco2e: number) => {
  if (!(bucket.totalTco2e > 0)) return '—'
  if (!(tco2e > 0)) return '—'
  const pct = (tco2e / bucket.totalTco2e) * 100
  return `${tco2e.toFixed(2)} (${pct.toFixed(0)}%)`
}

export const ReportingDataQualityMix: FunctionComponent<{ mix: IDataQualityMix }> = ({ mix }) => {
  const tiers = mix.tiers.filter(tier => tier !== 'Unclassified' || COLUMNS.some(column => (mix[column.key].byTier['Unclassified'] ?? 0) > 0))

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Data quality (tCO<sub>2</sub>e)</TableCell>
              {COLUMNS.map(column => (
                <TableCell key={column.key} align="right" sx={{ fontWeight: 600 }}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tiers.map(tier => (
              <TableRow key={tier}>
                <TableCell>{tier}</TableCell>
                {COLUMNS.map(column => (
                  <TableCell key={column.key} align="right">{cellValue(mix[column.key], mix[column.key].byTier[tier] ?? 0)}</TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
              {COLUMNS.map(column => (
                <TableCell key={column.key} align="right" sx={{ fontWeight: 600 }}>
                  {mix[column.key].totalTco2e > 0 ? mix[column.key].totalTco2e.toFixed(2) : '—'}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Of which spend-based</TableCell>
              {COLUMNS.map(column => (
                <TableCell key={column.key} align="right">{cellValue(mix[column.key], mix[column.key].spendBasedTco2e)}</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Of which estimated</TableCell>
              {COLUMNS.map(column => (
                <TableCell key={column.key} align="right">{cellValue(mix[column.key], mix[column.key].estimatedTco2e)}</TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell>Of which provisional factor year</TableCell>
              {COLUMNS.map(column => (
                <TableCell key={column.key} align="right">{cellValue(mix[column.key], mix[column.key].provisionalTco2e)}</TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
        Shares of each reported total by data-quality tier, following the GHG Protocol method
        preference (supplier-specific data, then activity data with average factors, then
        spend-based estimates). Shares describe how figures were calculated, not their accuracy.
        Figures follow the same selection and boundary rules as the scope totals above, so each
        column reconciles with the reported number.
      </Typography>
    </>
  )
}
